import { useCallback, useEffect, useState } from 'react'

import api from 'common/api'

import { createURLSeachParamsFromAPIFilters } from '../filter-state-to-api-query'
import { APIFilters, ExploreHighSchool, ResultOptions } from './types'

interface ResponseBody {
  total: number
  highSchools: ExploreHighSchool[]
  maxExportLimit: number
  center?: {
    lat: number
    lng: number
  }
}

export default function useGetHighschoolsExplore({
  resultOptions,
  filters,
  enabled,
}: {
  resultOptions: ResultOptions
  filters: APIFilters
  enabled: boolean
  viewId: string
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<ResponseBody | null>(null)
  const [error, setError] = useState<unknown>(null)

  const filterParams = createURLSeachParamsFromAPIFilters(filters)
  filterParams.set('from', (resultOptions.from || 0).toString())
  filterParams.set('limit', resultOptions.limit.toString())
  filterParams.set('sort', resultOptions.sortKey)
  filterParams.set('sortDir', resultOptions.sortDirection)
  const query = filterParams.toString()

  const fetch = useCallback(
    async (requestStatus: { active: boolean }) => {
      if (!enabled) return
      setIsSuccess(false)
      setIsLoading(true)
      setIsError(false)
      setError(null)

      const url = `/api/highschools/explore?${query}`

      try {
        const response = await api.get<ResponseBody>(url, {})
        if (requestStatus.active === false) return
        setData(response)
        setIsLoading(false)
        setIsSuccess(true)
      } catch (e) {
        if (requestStatus.active === false) return
        setIsLoading(false)
        setIsError(true)
        setError(e)
        setIsSuccess(false)
      }
    },
    [enabled, query],
  )

  useEffect(() => {
    const requestStatus = { active: true }
    fetch(requestStatus)

    return () => {
      requestStatus.active = false
    }
  }, [fetch])

  const applyTargets = useCallback((targets: { hsId: string; targeted: boolean }[]) => {
    const targetedById = new Map(targets.map((t) => [t.hsId, t.targeted]))
    setData((prev) =>
      prev
        ? {
            ...prev,
            highSchools: prev.highSchools.map((hs) =>
              targetedById.has(hs.id) ? { ...hs, target: targetedById.get(hs.id) } : hs,
            ),
          }
        : prev,
    )
  }, [])

  return { isLoading, isSuccess, isError, error, data, applyTargets }
}
